import React, {useCallback, useMemo, useRef, useState} from 'react';
import {Box} from '@mui/material';
import {useParams} from 'react-router-dom';
import {useGetTerritoireQuery} from '../api.js';
import {indexer} from '../selectors/index.js';
import {useCollecte} from '../collecte.js';
import {toCsv} from '../export.js';
import Coquille from '../components/Coquille.js';
import DashLignePeriode, {DashLignePeriodeChargement} from '../components/dashboard/DashLignePeriode.js';
import DashAlertes from '../components/dashboard/DashAlertes.js';
import {Bandeau, BandeauCollecte, BandeauLavage, BandeauParc} from '../components/dashboard/DashBandeaux.js';
import {
    collecteParCommune, csvCommunes, serieHebdo, syntheseCollecte, tuilesAlertesCollecte,
} from '../components/dashboard/collecte.dashboard.js';
import {evenementsRetenus} from '../selectors/etat.js';
import {DashChargement, DashLegendeAlertes, MAIN_SX} from '../components/dashboard/DashEtats.js';
import {
    calculerDashboard, dateFr, periodes as listePeriodes, requeteCarte, tuilesAlertes,
} from '../components/dashboard/dashboard.selectors.js';

// PIL-01 lot 5 : écran Dashboard territoire (Dashboard.dc.html, -Vide, -Chargement).
// Lot 9 : trois bandeaux (service de lavage, état du parc, collecte si le bouton est à 1).

const aujourdhuiLocal = () => new Date().toLocaleDateString('sv-SE'); // Y-m-d, fuseau local

function telecharger(contenu, nom) {
    const url = URL.createObjectURL(new Blob([contenu], {type: 'text/csv;charset=utf-8'}));
    const a = document.createElement('a');
    a.href = url;
    a.download = nom;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
}

export default function Dashboard() {
    const {slug} = useParams();
    const {data, isLoading, isError} = useGetTerritoireQuery(slug);
    const [periodeId, setPeriodeId] = useState('2026');
    // Circuits (patrimoines) retenus : null = tous (état initial), sinon liste d'ids. Même paramètre `patrimoines` que la carte.
    const [choix, setChoix] = useState(null);
    const selectRef = useRef(null);

    const aujourdhui = useMemo(aujourdhuiLocal, []);
    const [collecte] = useCollecte();
    const index = useMemo(() => (data ? indexer(data, {collecte}) : null), [data, collecte]);
    const periodes = useMemo(() => listePeriodes(aujourdhui, index ? index.bornes : null), [aujourdhui, index]);
    const periode = periodes.find((p) => p.id === periodeId) || periodes[0];
    const tousIds = useMemo(() => (index ? [...index.patrimoineById.keys()].sort((a, b) => a - b) : []), [index]);
    const patrimoines = choix || tousIds;
    const groupes = useMemo(() => {
        const g = {intra: {id: 'intra', libelle: 'Intramuros', ids: []}, extra: {id: 'extra', libelle: 'Extramuros', ids: []}};
        if (index) for (const p of index.patrimoineById.values()) if (g[p.groupe]) g[p.groupe].ids.push(p.id);
        return g;
    }, [index]);
    const params = useMemo(() => ({patrimoines, du: periode.du, au: periode.au}), [patrimoines, periode]);

    const calcul = useMemo(() => (index ? calculerDashboard(index, params) : null), [index, params]);
    const pavsSelection = calcul ? calcul.pavs : null; // tableau stable tant que la sélection de circuits ne change pas
    const tuiles = useMemo(() => (index && pavsSelection ? tuilesAlertes(index, aujourdhui, {pavs: pavsSelection}) : []), [index, aujourdhui, pavsSelection]);

    // Lien vers la carte : période et patrimoines du dashboard, sauf surcharge (alertes : à date, sur les circuits sélectionnés).
    const lien = useCallback((surcharge, parametres = params) => `/pilotage/${slug}/carte?${requeteCarte(index, aujourdhui, parametres, surcharge)}`, [index, aujourdhui, params, slug]);
    const tuilesCollecte = useMemo(() => (index && collecte && pavsSelection ? tuilesAlertesCollecte(index, aujourdhui, pavsSelection) : []), [index, collecte, aujourdhui, pavsSelection]);
    const liensAlertes = useMemo(() => {
        if (!index) return {};
        const tous = {patrimoines, du: '', au: aujourdhui};
        return Object.fromEntries([...tuiles, ...tuilesCollecte].filter((t) => t.filtre).map((t) => [t.id, lien(t.filtre, tous)]));
    }, [index, tuiles, tuilesCollecte, lien, aujourdhui, patrimoines]);
    const tuilesParc = useMemo(() => ['enDefaut', 'condamnees', 'signalements'].map((id) => tuiles.find((t) => t.id === id)).filter(Boolean), [tuiles]);

    // Séries hebdomadaires (lavages ; tonnage et remplissage si le bouton Collecte est à 1), tableau par commune, tuiles collecte.
    const du = periode.du || '';
    const retenus = useMemo(() => (index && pavsSelection ? evenementsRetenus(index, pavsSelection, {du, au: periode.au, at: periode.au}) : []), [index, pavsSelection, du, periode.au]);
    const courbeLavages = useMemo(() => serieHebdo(retenus, du, periode.au, 'lavages'), [retenus, du, periode.au]);
    const dataCollecte = useMemo(() => {
        if (!index || !collecte || !pavsSelection) return null;
        return {
            synthese: syntheseCollecte(pavsSelection, index, du, periode.au),
            tonnage: serieHebdo(retenus, du, periode.au, 'tonnage'),
            remplissage: serieHebdo(retenus, du, periode.au, 'remplissage'),
            quartiers: collecteParCommune(pavsSelection, index, du, periode.au),
        };
    }, [index, collecte, pavsSelection, retenus, du, periode.au]);

    const changerGroupe = (groupe, coche) => {
        const suivant = new Set(patrimoines);
        groupes[groupe].ids.forEach((id) => (coche ? suivant.add(id) : suivant.delete(id)));
        setChoix([...suivant].sort((a, b) => a - b));
    };
    const exporter = () => {
        const date = (data.territoire && data.territoire.exportedAt ? data.territoire.exportedAt.slice(0, 10) : aujourdhui);
        telecharger(toCsv(calcul.pavs, calcul.etatsLavage, index), `pilotage-${slug}-${periode.du || 'debut'}_${periode.au}-${date}.csv`);
    };

    const exporterCollecte = () => {
        const date = (data.territoire && data.territoire.exportedAt ? data.territoire.exportedAt.slice(0, 10) : aujourdhui);
        telecharger(csvCommunes(dataCollecte.quartiers), `pilotage-${slug}-collecte-${periode.du || 'debut'}_${periode.au}-${date}.csv`);
    };

    const nom = (data && data.territoire && data.territoire.nom) || '';
    const chargement = isLoading || (!index && !isError);
    const libellePeriode = periode.id === 'tout' ? 'toute la période' : `campagne ${periode.id}`;

    let corps;
    let barre;
    if (chargement || isError || !index) {
        barre = <DashLignePeriodeChargement nom={nom || 'Territoire'}/>;
        corps = chargement ? <DashChargement collecte={collecte}/> : (
            <Box component="main" sx={MAIN_SX}>
                <Box role="alert" sx={{p: '32px', textAlign: 'center', color: '#B22222', fontWeight: 700}}>Territoire indisponible</Box>
            </Box>
        );
    } else {
        barre = (
            <DashLignePeriode
                nom={nom}
                index={index}
                groupes={groupes}
                patrimoines={patrimoines}
                onPatrimoines={setChoix}
                onGroupe={changerGroupe}
                periodes={periodes}
                periodeId={periode.id}
                onPeriode={setPeriodeId}
                selectRef={selectRef}
                donneesAu={dateFr(data.territoire && data.territoire.exportedAt)}
                onExport={exporter}
                exportActif={!calcul.vide && calcul.pavs.length > 0}
                onExportCollecte={dataCollecte && dataCollecte.synthese.nbEvenements > 0 ? exporterCollecte : null}
            />
        );
        corps = (
            <Box component="main" sx={MAIN_SX}>
                <BandeauLavage
                    colonnes={calcul.colonnes}
                    rse={calcul.rse}
                    courbe={courbeLavages}
                    tuileImpossibles={tuiles.find((t) => t.id === 'impossiblesConsecutifs')}
                    lienImpossibles={liensAlertes}
                    liens={{lavage: lien({acte: 'lavage'}), impossibles: lien({acte: 'lavage', etats: ['imp']})}}
                    vide={calcul.vide}
                    videProps={{
                        periode: `la période du ${dateFr(periode.du)} au ${dateFr(periode.au)}`,
                        onVoirCampagne: () => setPeriodeId('2026'),
                        onAutrePeriode: () => selectRef.current && selectRef.current.focus(),
                    }}
                />
                {calcul.vide ? (
                    <Bandeau titre="État du parc" id="bandeau-parc">
                        <DashLegendeAlertes/>
                        <DashAlertes tuiles={tuilesParc} liens={liensAlertes} colonnes={3}/>
                    </Bandeau>
                ) : (
                    <BandeauParc
                        colonnes={calcul.colonnes}
                        elementN1={calcul.repartitions.elementN1}
                        condamnees={(tuiles.find((t) => t.id === 'condamnees') || {}).nb}
                        repartitions={calcul.repartitions}
                        tuilesAlertes={tuilesParc}
                        liensAlertes={liensAlertes}
                        lienCommune={(cle) => lien({acte: 'maint', commune: cle, etats: ['pb']})}
                        lienPatrimoine={(id) => lien({acte: 'maint'}, {...params, patrimoines: [id]})}
                        liens={{maintenance: lien({acte: 'maint'}), enDefaut: lien({acte: 'maint', etats: ['pb']}), graffitis: lien({acte: 'graffiti'}), condamnees: liensAlertes.condamnees}}
                    />
                )}
                {dataCollecte && (
                    <BandeauCollecte
                        synthese={dataCollecte.synthese}
                        vide={dataCollecte.synthese.nbEvenements === 0}
                        courbeTonnage={dataCollecte.tonnage}
                        courbeRemplissage={dataCollecte.remplissage}
                        quartiers={dataCollecte.quartiers}
                        lienCommune={(cle) => lien({acte: 'collecte', style: 'tonnage', ...(cle ? {commune: cle} : {})})}
                        tuilesAlertes={tuilesCollecte}
                        liensAlertes={liensAlertes}
                        lienCollecte={lien({acte: 'collecte', style: 'tonnage'})}
                        lienDeclasses={lien({acte: 'collecte', style: 'abribacs', etats: ['imp']})}
                    />
                )}
            </Box>
        );
    }

    return (
        <Coquille slug={slug} onglet="dashboard" index={index} sansMarge>
            {barre}
            {corps}
        </Coquille>
    );
}
